import { useEffect, useState } from "react";
import { Plus, Search, Building2, Calendar, Clock, Users, Play, Pause, CheckCircle2, XCircle, MoreVertical, MapPin, Euro, LayoutGrid, List } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useObrasContext } from "@/contexts/ObrasContext";
import { useAuth } from "@/contexts/AuthContext";
import { ObraForm } from "@/components/obras/ObraForm";
import { ObraDetail } from "@/components/obras/ObraDetail";
import { ObraEntregaDialog } from "@/components/obras/ObraEntregaDialog";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { computeObraInsights } from "@/lib/obraInsights";
import { ObraConclusiones } from "@/components/obras/ObraConclusiones";

type EstadoObra = 'activa' | 'pausada' | 'finalizada' | 'cancelada';

const ESTADO_FILTERS: { value: EstadoObra | 'all'; label: string; icon: any; color: string }[] = [
  { value: 'all', label: 'Todas', icon: Building2, color: 'bg-muted text-foreground hover:bg-muted/80' },
  { value: 'activa', label: 'Activas', icon: Play, color: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/25 border-emerald-500/30' },
  { value: 'pausada', label: 'Pausadas', icon: Pause, color: 'bg-amber-500/15 text-amber-700 dark:text-amber-400 hover:bg-amber-500/25 border-amber-500/30' },
  { value: 'finalizada', label: 'Finalizadas', icon: CheckCircle2, color: 'bg-blue-500/15 text-blue-700 dark:text-blue-400 hover:bg-blue-500/25 border-blue-500/30' },
  { value: 'cancelada', label: 'Canceladas', icon: XCircle, color: 'bg-destructive/15 text-destructive hover:bg-destructive/25 border-destructive/30' },
];

const ESTADO_BADGE: Record<EstadoObra, string> = {
  activa: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30',
  pausada: 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30',
  finalizada: 'bg-blue-500/15 text-blue-700 dark:text-blue-400 border-blue-500/30',
  cancelada: 'bg-destructive/15 text-destructive border-destructive/30',
};

export default function Obras() {
  const { obras, loading, updateObra } = useObrasContext();
  const { perfil } = useAuth();
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState("");
  const [estadoFilter, setEstadoFilter] = useState<EstadoObra | 'all'>("all");
  const [yearFilter, setYearFilter] = useState<string>("all");
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedObra, setSelectedObra] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [entregaTarget, setEntregaTarget] = useState<{ id: string; estado: string } | null>(null);

  const isAdmin = perfil?.rol === 'admin' || perfil?.rol === 'gestor';

  const [obrasConDocs, setObrasConDocs] = useState<Set<string>>(new Set());
  useEffect(() => {
    const finalizadasIds = obras.filter(o => o.estado === 'finalizada').map(o => o.id);
    if (finalizadasIds.length === 0) {
      setObrasConDocs(new Set());
      return;
    }
    (async () => {
      const { data } = await (supabase as any)
        .from('obra_entrega_documentos')
        .select('obra_id')
        .in('obra_id', finalizadasIds);
      setObrasConDocs(new Set((data ?? []).map((d: any) => d.obra_id)));
    })();
  }, [obras]);

  const counts = {
    all: obras.length,
    activa: obras.filter(o => o.estado === 'activa').length,
    pausada: obras.filter(o => o.estado === 'pausada').length,
    finalizada: obras.filter(o => o.estado === 'finalizada').length,
    cancelada: obras.filter(o => o.estado === 'cancelada').length,
  };

  const availableYears = Array.from(
    new Set(obras.map(o => new Date(o.fecha_inicio).getFullYear()))
  ).sort((a, b) => b - a);

  const filteredObras = obras.filter(obra => {
    const matchesSearch = obra.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         obra.numero_obra?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         obra.cliente?.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         obra.descripcion?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesEstado = estadoFilter === "all" || obra.estado === estadoFilter;
    const matchesYear = yearFilter === "all" ||
      new Date(obra.fecha_inicio).getFullYear().toString() === yearFilter;
    return matchesSearch && matchesEstado && matchesYear;
  });

  const getProgressValue = (obra: any) => {
    if (obra.horas_estimadas === 0) return 0;
    return Math.min((obra.horas_consumidas / obra.horas_estimadas) * 100, 100);
  };

  const handleChangeEstado = async (e: React.MouseEvent, obraId: string, nuevoEstado: EstadoObra) => {
    e.stopPropagation();
    try {
      await updateObra(obraId, { estado: nuevoEstado } as any);
      toast({
        title: "Estado actualizado",
        description: `La obra ahora está ${nuevoEstado}`,
      });
      if (nuevoEstado === 'finalizada') {
        await (supabase as any).from('obras').update({ entrega_pospuesta: false }).eq('id', obraId);
        setEntregaTarget({ id: obraId, estado: 'finalizada' });
      }
    } catch {
      toast({
        title: "Error",
        description: "No se pudo cambiar el estado",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Cargando obras...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 sm:p-6 space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 sm:gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-2">
            <Building2 className="h-7 w-7 sm:h-8 sm:w-8 shrink-0" />
            <span className="truncate">Gestión de Obras</span>
          </h1>
          <p className="text-sm text-muted-foreground">
            Administra las obras y supervisa el progreso de cada proyecto
          </p>
        </div>
        
        {isAdmin && (
          <Dialog open={showCreateForm} onOpenChange={setShowCreateForm}>
            <DialogTrigger asChild>
              <Button className="w-full sm:w-auto">
                <Plus className="h-4 w-4 mr-2" />
                Nueva Obra
              </Button>
            </DialogTrigger>
            <DialogContent className="w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
              <DialogHeader>
                <DialogTitle>Crear Nueva Obra</DialogTitle>
              </DialogHeader>
              <ObraForm onSuccess={() => setShowCreateForm(false)} />
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 sm:gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Obras</CardTitle>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold">{obras.length}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Obras Activas</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold">{counts.activa}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Horas Estimadas</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold">
              {obras.reduce((sum, obra) => sum + obra.horas_estimadas, 0).toFixed(1)}h
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Horas Consumidas</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold">
              {obras.reduce((sum, obra) => sum + obra.horas_consumidas, 0).toFixed(1)}h
            </div>
          </CardContent>
        </Card>

        {isAdmin && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Coste Consumido</CardTitle>
              <Euro className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-xl sm:text-2xl font-bold">
                {new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(
                  obras.reduce((sum, obra) => sum + (obra.coste_consumido || 0), 0)
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Search + Year + View toggle */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar obras por nombre, cliente o descripción..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={yearFilter} onValueChange={setYearFilter}>
          <SelectTrigger className="w-full sm:w-[140px]">
            <SelectValue placeholder="Año" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los años</SelectItem>
            {availableYears.map((y) => (
              <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="inline-flex rounded-md border bg-background p-0.5 self-start sm:self-auto">
          <Button
            type="button"
            variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
            size="sm"
            className="h-8 px-2"
            onClick={() => setViewMode('grid')}
            aria-label="Vista de tarjetas"
          >
            <LayoutGrid className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant={viewMode === 'list' ? 'secondary' : 'ghost'}
            size="sm"
            className="h-8 px-2"
            onClick={() => setViewMode('list')}
            aria-label="Vista de lista"
          >
            <List className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Visual Filters */}
      <div className="flex flex-wrap gap-2">
        {ESTADO_FILTERS.map((f) => {
          const Icon = f.icon;
          const isActive = estadoFilter === f.value;
          const count = counts[f.value];
          return (
            <button
              key={f.value}
              onClick={() => setEstadoFilter(f.value)}
              className={cn(
                "inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium transition-all",
                f.color,
                isActive
                  ? "ring-2 ring-offset-2 ring-offset-background ring-primary sm:scale-105"
                  : "border-transparent opacity-80 hover:opacity-100"
              )}
            >
              <Icon className="h-4 w-4" />
              {f.label}
              <span className="ml-1 px-2 py-0.5 rounded-full bg-background/60 text-xs font-bold">
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Obras Grid */}
      {viewMode === 'grid' ? (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredObras.map((obra) => (
          <Card 
            key={obra.id} 
            className="cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => setSelectedObra(obra.id)}
          >
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-2">
                <div className="space-y-1 min-w-0 flex-1">
                  {obra.numero_obra && (
                    <p className="text-xs font-mono text-muted-foreground">{obra.numero_obra}</p>
                  )}
                  <CardTitle className="text-lg line-clamp-1">{obra.nombre}</CardTitle>
                  <CardDescription className="line-clamp-1">
                    {obra.cliente?.nombre}
                  </CardDescription>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Badge variant="outline" className={cn("capitalize", ESTADO_BADGE[obra.estado as EstadoObra])}>
                    {obra.estado}
                  </Badge>
                  {isAdmin && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                        {obra.estado !== 'activa' && (
                          <DropdownMenuItem onClick={(e) => handleChangeEstado(e, obra.id, 'activa')}>
                            <Play className="h-4 w-4 mr-2 text-emerald-600" />
                            Marcar como activa
                          </DropdownMenuItem>
                        )}
                        {obra.estado !== 'pausada' && obra.estado !== 'finalizada' && obra.estado !== 'cancelada' && (
                          <DropdownMenuItem onClick={(e) => handleChangeEstado(e, obra.id, 'pausada')}>
                            <Pause className="h-4 w-4 mr-2 text-amber-600" />
                            Pausar obra
                          </DropdownMenuItem>
                        )}
                        {obra.estado !== 'finalizada' && (
                          <DropdownMenuItem onClick={(e) => handleChangeEstado(e, obra.id, 'finalizada')}>
                            <CheckCircle2 className="h-4 w-4 mr-2 text-blue-600" />
                            Finalizar obra
                          </DropdownMenuItem>
                        )}
                        {obra.estado !== 'cancelada' && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={(e) => handleChangeEstado(e, obra.id, 'cancelada')}
                              className="text-destructive focus:text-destructive"
                            >
                              <XCircle className="h-4 w-4 mr-2" />
                              Cancelar obra
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </div>
            </CardHeader>
            
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Progreso</span>
                  <span>{obra.horas_consumidas}h / {obra.horas_estimadas}h</span>
                </div>
                <Progress value={getProgressValue(obra)} className="h-2" />
                {isAdmin && (
                  <div className="flex items-center justify-between text-sm pt-1">
                    <span className="flex items-center gap-1 text-muted-foreground">
                      <Euro className="h-3.5 w-3.5" />
                      Coste consumido
                    </span>
                    <span className="font-semibold">
                      {new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(obra.coste_consumido || 0)}
                    </span>
                  </div>
                )}
              </div>
              
              {obra.direccion && (
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(obra.direccion)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="flex items-start text-sm text-muted-foreground hover:text-primary hover:underline"
                >
                  <MapPin className="h-4 w-4 mr-1 mt-0.5 shrink-0" />
                  <span className="line-clamp-2">{obra.direccion}</span>
                </a>
              )}

              <div className="flex items-center text-sm text-muted-foreground">
                <Calendar className="h-4 w-4 mr-1" />
                Inicio: {new Date(obra.fecha_inicio).toLocaleDateString()}
              </div>
              
              {obra.descripcion && (
                <p className="text-sm text-muted-foreground line-clamp-2">
                  {obra.descripcion}
                </p>
              )}

              {obra.estado === 'finalizada' && (() => {
                const { conclusiones } = computeObraInsights({
                  estado: obra.estado,
                  horas_estimadas: Number(obra.horas_estimadas) || 0,
                  horas_consumidas: Number(obra.horas_consumidas) || 0,
                  coste_mano_obra_estimado: Number(obra.coste_mano_obra_estimado) || 0,
                  coste_consumido: Number(obra.coste_consumido) || 0,
                  precio_venta: obra.precio_venta ?? null,
                  tiene_docs_entrega: obrasConDocs.has(obra.id),
                });
                return <ObraConclusiones conclusiones={conclusiones} hideFinancial={!isAdmin} />;
              })()}

              {/* Quick action buttons */}
              {isAdmin && obra.estado !== 'finalizada' && obra.estado !== 'cancelada' && (
                <div className="flex gap-2 pt-2 border-t">
                  {obra.estado === 'activa' ? (
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={(e) => handleChangeEstado(e, obra.id, 'pausada')}
                    >
                      <Pause className="h-3.5 w-3.5 mr-1" />
                      Pausar
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={(e) => handleChangeEstado(e, obra.id, 'activa')}
                    >
                      <Play className="h-3.5 w-3.5 mr-1" />
                      Reanudar
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={(e) => handleChangeEstado(e, obra.id, 'finalizada')}
                  >
                    <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                    Finalizar
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
      ) : (
        <Card>
          <div className="divide-y">
            {filteredObras.map((obra) => {
              const insights = obra.estado === 'finalizada'
                ? computeObraInsights({
                    estado: obra.estado,
                    horas_estimadas: Number(obra.horas_estimadas) || 0,
                    horas_consumidas: Number(obra.horas_consumidas) || 0,
                    coste_mano_obra_estimado: Number(obra.coste_mano_obra_estimado) || 0,
                    coste_consumido: Number(obra.coste_consumido) || 0,
                    precio_venta: obra.precio_venta ?? null,
                    tiene_docs_entrega: obrasConDocs.has(obra.id),
                  })
                : null;
              return (
                <div
                  key={obra.id}
                  className="flex items-center gap-3 p-3 sm:p-4 hover:bg-muted/40 cursor-pointer"
                  onClick={() => setSelectedObra(obra.id)}
                >
                  <Building2 className="h-5 w-5 text-muted-foreground shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      {obra.numero_obra && (
                        <span className="text-[10px] font-mono text-muted-foreground">{obra.numero_obra}</span>
                      )}
                      <span className="font-medium truncate">{obra.nombre}</span>
                      <Badge variant="outline" className={cn("capitalize text-xs", ESTADO_BADGE[obra.estado as EstadoObra])}>
                        {obra.estado}
                      </Badge>
                    </div>
                    <div className="text-xs text-muted-foreground flex items-center gap-3 flex-wrap mt-0.5">
                      {obra.cliente?.nombre && <span className="truncate">{obra.cliente.nombre}</span>}
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {new Date(obra.fecha_inicio).toLocaleDateString()}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {Number(obra.horas_consumidas).toFixed(1)}h / {Number(obra.horas_estimadas).toFixed(1)}h
                      </span>
                      {isAdmin && (
                        <span className="flex items-center gap-1">
                          <Euro className="h-3 w-3" />
                          {new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(obra.coste_consumido || 0)}
                        </span>
                      )}
                    </div>
                    {insights && insights.conclusiones.length > 0 && (
                      <ObraConclusiones conclusiones={insights.conclusiones} hideFinancial={!isAdmin} className="mt-2 border-t-0 pt-1" />
                    )}
                  </div>
                  <div className="hidden sm:block w-32">
                    <Progress value={getProgressValue(obra)} className="h-1.5" />
                  </div>
                  {isAdmin && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={(e) => e.stopPropagation()}>
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                        {obra.estado !== 'activa' && (
                          <DropdownMenuItem onClick={(e) => handleChangeEstado(e, obra.id, 'activa')}>
                            <Play className="h-4 w-4 mr-2 text-emerald-600" />Marcar como activa
                          </DropdownMenuItem>
                        )}
                        {obra.estado !== 'pausada' && obra.estado !== 'finalizada' && obra.estado !== 'cancelada' && (
                          <DropdownMenuItem onClick={(e) => handleChangeEstado(e, obra.id, 'pausada')}>
                            <Pause className="h-4 w-4 mr-2 text-amber-600" />Pausar obra
                          </DropdownMenuItem>
                        )}
                        {obra.estado !== 'finalizada' && (
                          <DropdownMenuItem onClick={(e) => handleChangeEstado(e, obra.id, 'finalizada')}>
                            <CheckCircle2 className="h-4 w-4 mr-2 text-blue-600" />Finalizar obra
                          </DropdownMenuItem>
                        )}
                        {obra.estado !== 'cancelada' && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={(e) => handleChangeEstado(e, obra.id, 'cancelada')} className="text-destructive focus:text-destructive">
                              <XCircle className="h-4 w-4 mr-2" />Cancelar obra
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      )}
      {filteredObras.length === 0 && (
        <div className="text-center py-12">
          <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">No se encontraron obras</h3>
          <p className="text-muted-foreground mb-4">
            {searchTerm || estadoFilter !== "all" 
              ? "Prueba ajustando los filtros de búsqueda"
              : "Comienza creando tu primera obra"
            }
          </p>
          {isAdmin && !searchTerm && estadoFilter === "all" && (
            <Button onClick={() => setShowCreateForm(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Crear Primera Obra
            </Button>
          )}
        </div>
      )}

      {/* Obra Detail Dialog */}
      {selectedObra && (
        <ObraDetail 
          obraId={selectedObra}
          isOpen={!!selectedObra}
          onClose={() => setSelectedObra(null)}
        />
      )}

      {/* Diálogo de entrega tras finalizar */}
      {entregaTarget && (
        <ObraEntregaDialog
          obraId={entregaTarget.id}
          obraEstado={entregaTarget.estado}
          open={!!entregaTarget}
          onClose={() => setEntregaTarget(null)}
        />
      )}
    </div>
  );
}
